import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripePromise } from '../../services/stripe';
import { toast } from 'react-toastify';

interface CheckoutFormProps {
  onSuccess: (paymentIntentId: string) => void;
  onClose: () => void;
  totalAmount: number;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ onSuccess, onClose, totalAmount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe or Elements not loaded');
      return;
    }

    setLoading(true);
    console.log('🔄 Starting payment confirmation...');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/payment-success',
        },
        redirect: 'if_required',
      });

      console.log('💳 Payment response:', { error, paymentIntent });

      if (error) {
        console.error('❌ Payment error:', error);
        toast.error(error.message || 'Payment failed', {
          position: 'top-center',
          autoClose: 4000,
        });
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        toast.success('Payment successful! Processing your order...', {
          position: 'top-center',
          autoClose: 2000,
        });
        onSuccess(paymentIntent.id);
      } else {
        console.warn('⚠️ Unexpected payment status:', paymentIntent?.status);
        toast.error('Payment status unclear. Please contact support.', {
          position: 'top-center',
          autoClose: 4000,
        });
      }
    } catch (err) {
      console.error('💥 Payment processing exception:', err);
      toast.error('Payment processing failed', {
        position: 'top-center',
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{
      padding: '20px',
      maxHeight: '80vh',
      overflowY: 'auto'
    }}>
      <div style={{ marginBottom: '20px' }}>
        <PaymentElement />
      </div>

      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '24px'
      }}>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: '#FFFFFF',
            color: '#1A1A1A',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
          }}
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={!stripe || loading}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: !stripe || loading ? '#9CA3AF' : '#4CAF50',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: !stripe || loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {loading ? (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: 'spin 1s linear infinite' }}
              >
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
              </svg>
              Processing...
            </>
          ) : (
            `Pay €${totalAmount.toFixed(2)}`
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

interface CheckoutModalProps {
  clientSecret: string;
  onSuccess: (paymentIntentId: string) => void;
  onClose: () => void;
  totalAmount: number;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  clientSecret,
  onSuccess,
  onClose,
  totalAmount
}) => {
  const stripePromise = getStripePromise();
  const stripeLoading = false;

  if (stripeLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        <div style={{ color: '#FFFFFF' }}>Loading payment...</div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            textAlign: 'center'
          }}
        >
          <p style={{ color: '#EF4444', margin: 0 }}>
            Stripe payment is not configured. Please contact support.
          </p>
          <button
            onClick={onClose}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#B8860B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1A1A1A'
            }}
          >
            Complete Payment
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#6B7280'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm onSuccess={onSuccess} onClose={onClose} totalAmount={totalAmount} />
        </Elements>
      </div>
    </div>
  );
};
